import { DestroyRef, Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Titre de l'onglet du navigateur : « Boutique - Page » (ex. « SKOODA - Factures »).
 *
 * La page est, par ordre de priorité :
 *  1. le `title` déclaré sur la route (le plus profond) ;
 *  2. le libellé du lien de la barre latérale dont l'adresse est le plus long préfixe
 *     de l'URL courante — une fiche `/moni/factures/<id>` prend donc « Factures ».
 *     Valable pour toutes les barres latérales (menu principal et modules), sans
 *     dépendre de leur `routerLinkActive` (exact dans le menu principal) ;
 *  3. le nom du module (badge de la barre du haut).
 *
 * Démarré par la barre du haut, présente dans tous les gabarits du back-office.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private readonly title = inject(Title);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);

  private shopName: string | null = null;
  private moduleLabel: string | null = null;
  private started = false;
  private retry: ReturnType<typeof setTimeout> | null = null;

  start(destroyRef: DestroyRef): void {
    if (this.started) {
      this.refresh();
      return;
    }
    this.started = true;
    const sub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.refresh());
    destroyRef.onDestroy(() => {
      sub.unsubscribe();
      this.started = false;
    });
    this.refresh();
  }

  setShopName(name: string | null): void {
    this.shopName = name?.trim() || null;
    this.refresh();
  }

  setModuleLabel(label: string | null): void {
    this.moduleLabel = label?.trim() || null;
    this.refresh();
  }

  /** Recalcule après le rendu : la barre latérale peut arriver après la navigation. */
  private refresh(): void {
    if (this.retry) clearTimeout(this.retry);
    setTimeout(() => {
      const found = this.apply();
      if (!found) {
        this.retry = setTimeout(() => this.apply(), 1000);
      }
    });
  }

  /** @returns vrai si un nom de page précis a été trouvé (route ou barre latérale). */
  private apply(): boolean {
    const fromRoute = this.routeTitle(this.router.routerState.snapshot.root);
    const fromSidebar = fromRoute ? null : this.sidebarLabel();
    const page = fromRoute ?? fromSidebar ?? this.moduleLabel;
    this.title.setTitle(buildPageTitle(this.shopName, page));
    return !!(fromRoute || fromSidebar);
  }

  private routeTitle(snapshot: ActivatedRouteSnapshot): string | null {
    let found: string | null = null;
    let node: ActivatedRouteSnapshot | null = snapshot;
    while (node) {
      const t = node.title ?? node.data?.['title'];
      if (typeof t === 'string' && t.trim()) found = t.trim();
      node = node.firstChild;
    }
    return found;
  }

  private sidebarLabel(): string | null {
    const path = this.router.url.split(/[?#]/)[0].replace(/\/+$/, '');
    const anchors = Array.from(this.document.querySelectorAll<HTMLAnchorElement>('.layout-sidebar a[href]'));
    return bestSidebarLabel(
      path,
      anchors.map((a) => ({
        href: a.getAttribute('href') ?? '',
        label: (a.querySelector('.layout-menuitem-text, span')?.textContent ?? a.textContent ?? '').trim(),
      })),
    );
  }
}

/** « Boutique - Page », sans tiret orphelin quand l'une des deux manque. */
export function buildPageTitle(shop: string | null, page: string | null): string {
  const parts = [shop, page].map((p) => p?.trim()).filter((p): p is string => !!p);
  return parts.length ? parts.join(' - ') : 'MONICASE';
}

/** Libellé du lien dont l'adresse est le plus long préfixe (par segment) de `path`. */
export function bestSidebarLabel(path: string, links: { href: string; label: string }[]): string | null {
  let best: { len: number; label: string } | null = null;
  for (const { href, label } of links) {
    const target = href.split(/[?#]/)[0].replace(/\/+$/, '');
    if (!target || target === '/' || !label) continue;
    if (path !== target && !path.startsWith(target + '/')) continue;
    if (!best || target.length > best.len) best = { len: target.length, label };
  }
  return best?.label ?? null;
}
